import React from 'react'
import TextProcessor from "@/components/common/TextProcessor";
import ImageRenderer from "@/components/common/ImageRenderer";
import type { Block } from "@/sections/SectionRenderer";

interface Feature {
  label: string;
  description: string;
  icon: string;
  moreHref?: string;
  href?: string;
}

interface PrimaryFeaturesData extends Block {
  features: Feature[];
}

export function PrimaryFeatures({ data }: { data: PrimaryFeaturesData }) {
   const { id, features } = data;
   const shadowIconClass = "drop-shadow-pinkblue dark:drop-shadow-purpleright";

  // Hydration-safe button styles: no runtime conditionals in class strings.
  const btnBase =
    "absolute bottom-2 right-3 inline-flex items-center justify-center h-10 w-10 rounded-full " +
    "transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

  // Base chrome with dark variants (stable between SSR & CSR)
  const baseChrome =
    "bg-white/70 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 " +
    "border border-gray-200 dark:border-white/10 shadow-sm";

  // Interactive states expressed via dark: utilities (no isDarkMode branching)
  const interactiveStates =
    "hover:bg-gray-100/70 dark:hover:bg-gray-700/60 " +
    "hover:text-gray-700 dark:hover:text-indigo-200/80 " +
    "hover:border-gray-300 dark:hover:border-indigo-400/40 " +
    "focus-visible:ring-1 focus-visible:ring-indigo-300/60 dark:focus-visible:ring-indigo-300/50 " +
    "focus-visible:ring-offset-0";

  const activeBtnBase =
    btnBase +
    " " +
    baseChrome +
    " cursor-pointer backdrop-blur supports-[backdrop-filter]:backdrop-blur-md " +
    interactiveStates;

  const disabledBtnBase =
    btnBase +
    " " +
    baseChrome +
    " opacity-40 cursor-default pointer-events-none";

  return (
    <div id={id} className="pb-16 sm:pb-20">
      <div className="mx-auto px-6 lg:px-32">
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            {features.map((feature, idx) => {
              const link = feature.moreHref ?? feature.href ?? null;

              return (
                <div
                  key={`${feature.label}-${idx}`}
                  className="relative flex flex-col bg-card p-8 pb-16 rounded-xl"
                  role="group"
                  aria-labelledby={`feature-${idx}-title`}
                >
                  <dt className="flex flex-col items-center text-center">
                    <div className={`${shadowIconClass} transition-all duration-300 mb-4`}>
                      <ImageRenderer
                        name={feature.icon}
                        className="h-auto w-24 mx-auto relative"
                      />
                    </div>
                    <span
                      id={`feature-${idx}-title`}
                      className="block font-semibold tracking-wide text-primary text-xl md:text-2xl leading-8 md:leading-[2.25rem]"
                    >
                      <TextProcessor>{feature.label}</TextProcessor>
                    </span>
                  </dt>
                  <dd className="mt-2 text-center">
                    <p className="flex-auto text-base md:text-lg leading-7 md:leading-8">
                      <TextProcessor>{feature.description}</TextProcessor>
                    </p>
                  </dd>

                  {link ? (
                    <a
                      href={link}
                      aria-label={`Read more about ${feature.label}`}
                      className={activeBtnBase}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5 pointer-events-none"
                        aria-hidden="true"
                      >
                        <path d="M7 12h10M13 8l4 4-4 4" />
                      </svg>
                      <span className="sr-only">Open details</span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      aria-disabled="true"
                      aria-label={`More about ${feature.label} (details coming soon)`}
                      className={disabledBtnBase}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5 pointer-events-none opacity-70"
                        aria-hidden="true"
                      >
                        <path d="M7 12h10M13 8l4 4-4 4" />
                      </svg>
                      <span className="sr-only">Details disabled</span>
                    </button>
                  )}
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </div>
  );
}
